import type { FacilityProfile } from "./facility";
import {
  CUSTOM_SOCIAL_LINK_DEFAULTS,
  isSocialPresetNetwork,
  SOCIAL_LINK_DEFAULTS,
  SOCIAL_PRESET_NETWORKS,
  SOCIAL_STOCK_CATALOG_SLUG,
  type SocialPresetNetwork,
  MIN_SOCIAL_BLOCK_LINKS,
} from "./social-link-defaults";
import { socialBlockLink } from "./blocks/social";
import type {
  FacilityAssetKey,
  MediaRef,
  SocialBlockLink,
  SocialBlockLinkNetwork,
  SocialIconVariant,
  SocialNetwork,
} from "./types";

const stockMediaSourcesByCatalogSlug = new Map<string, string>();

export function registerEmailBuilderStockMediaCatalogSlug(
  catalogSlug: string,
  source: string,
): void {
  const trimmedSlug = catalogSlug.trim();
  const trimmedSource = source.trim();
  if (trimmedSlug.length === 0 || trimmedSource.length === 0) {
    return;
  }
  stockMediaSourcesByCatalogSlug.set(trimmedSlug, trimmedSource);
}

export function resolveSocialIconUrl(
  network: SocialNetwork,
  iconVariant: SocialIconVariant,
): string {
  if (iconVariant !== 'white') {
    return '';
  }
  const catalogSlug = SOCIAL_STOCK_CATALOG_SLUG[network];
  return stockMediaSourcesByCatalogSlug.get(catalogSlug) ?? '';
}

const libraryMediaSources = new Map<string, string>();

export function registerEmailBuilderLibraryMediaSource(
  mediaId: number | string,
  source: string,
): void {
  const trimmed = source.trim();
  if (trimmed.length === 0) {
    return;
  }
  libraryMediaSources.set(String(mediaId), trimmed);
}

export function resolveEmailBuilderLibraryMediaSource(mediaId: string): string {
  return libraryMediaSources.get(mediaId) ?? '';
}

export function clearEmailBuilderLibraryMediaSources(): void {
  libraryMediaSources.clear();
  stockMediaSourcesByCatalogSlug.clear();
}

export function isFacilityLogoMediaRef(
  ref: MediaRef | null,
): ref is { kind: 'facility'; assetKey: 'logo' | 'logoDark' } {
  return (
    ref?.kind === 'facility' &&
    (ref.assetKey === 'logo' || ref.assetKey === 'logoDark')
  );
}

function facilityAssetUrl(
  facility: FacilityProfile,
  key: FacilityAssetKey,
): string {
  if (key === 'logo' || key === 'logoDark') {
    return facility.logoUrl;
  }
  return '';
}

export function resolveMediaRef(
  ref: MediaRef | null,
  facility: FacilityProfile,
): string {
  if (!ref) {
    return '';
  }
  if (ref.kind === 'facility') {
    return facilityAssetUrl(facility, ref.assetKey);
  }
  if (ref.kind === 'library') {
    return resolveEmailBuilderLibraryMediaSource(ref.mediaId);
  }
  return ref.url;
}

export const SOCIAL_LABELS: Record<SocialNetwork, string> = {
  facebook: SOCIAL_LINK_DEFAULTS.facebook.title,
  instagram: SOCIAL_LINK_DEFAULTS.instagram.title,
  twitter: SOCIAL_LINK_DEFAULTS.twitter.title,
};

function isKnownSocialNetwork(
  network: SocialBlockLinkNetwork,
): network is SocialNetwork {
  return network !== 'custom';
}

export function availableSocialPresetNetworks(
  links: SocialBlockLink[],
): SocialPresetNetwork[] {
  const used = new Set(
    links
      .map(link => link.network)
      .filter(isSocialPresetNetwork),
  );
  return SOCIAL_PRESET_NETWORKS.filter(network => !used.has(network));
}

export function createPresetSocialBlockLink(
  network: SocialPresetNetwork,
): SocialBlockLink {
  const defaults = SOCIAL_LINK_DEFAULTS[network];
  return socialBlockLink(network, defaults.url, {
    title: defaults.title,
    alt: defaults.alt,
  });
}

export function createCustomSocialBlockLink(
  icon: SocialBlockLink['icon'] = null,
): SocialBlockLink {
  return socialBlockLink('custom', CUSTOM_SOCIAL_LINK_DEFAULTS.url, {
    title: CUSTOM_SOCIAL_LINK_DEFAULTS.title,
    alt: CUSTOM_SOCIAL_LINK_DEFAULTS.alt,
    icon,
  });
}

export function addPresetSocialBlockLink(
  links: SocialBlockLink[],
  network: SocialPresetNetwork,
): SocialBlockLink[] {
  if (links.some(link => link.network === network)) {
    return links;
  }
  return [...links, createPresetSocialBlockLink(network)];
}

export function addCustomSocialBlockLink(
  links: SocialBlockLink[],
  icon: SocialBlockLink['icon'] = null,
): SocialBlockLink[] {
  return [...links, createCustomSocialBlockLink(icon)];
}

export function removeSocialBlockLink(
  links: SocialBlockLink[],
  linkId: string,
): SocialBlockLink[] {
  if (links.length <= MIN_SOCIAL_BLOCK_LINKS) {
    return links;
  }
  const next = links.filter(link => link.id !== linkId);
  return next.length > 0 ? next : links;
}

export function moveSocialBlockLink(
  links: SocialBlockLink[],
  linkId: string,
  direction: -1 | 1,
): SocialBlockLink[] {
  const index = links.findIndex(link => link.id === linkId);
  if (index < 0) {
    return links;
  }
  const targetIndex = index + direction;
  if (targetIndex < 0 || targetIndex >= links.length) {
    return links;
  }
  const next = [...links];
  const [moved] = next.splice(index, 1);
  next.splice(targetIndex, 0, moved);
  return next;
}

export function patchSocialBlockLinkById(
  links: SocialBlockLink[],
  linkId: string,
  patch: Partial<Pick<SocialBlockLink, 'url' | 'title' | 'alt' | 'icon'>>,
): SocialBlockLink[] {
  return links.map(link => {
    if (link.id !== linkId) {
      return link;
    }

    const next: SocialBlockLink = { ...link };

    if (patch.url !== undefined) {
      next.url = patch.url;
    }

    if (patch.title !== undefined) {
      next.title = patch.title;
    }

    if (patch.alt !== undefined) {
      next.alt = patch.alt;
    }

    if (patch.icon !== undefined) {
      next.icon = patch.icon;
    }

    return next;
  });
}

export function resolveSocialLinkTitle(link: SocialBlockLink): string {
  const trimmed = link.title?.trim();
  if (trimmed && trimmed.length > 0) {
    return trimmed;
  }
  if (isKnownSocialNetwork(link.network)) {
    return SOCIAL_LABELS[link.network];
  }
  return CUSTOM_SOCIAL_LINK_DEFAULTS.title;
}

export function resolveSocialLinkIconUrl(
  link: SocialBlockLink,
  iconVariant: SocialIconVariant,
  facility: FacilityProfile,
): string {
  if (link.icon) {
    const customSource = resolveMediaRef(link.icon, facility);
    if (customSource.length > 0) {
      return customSource;
    }
  }

  if (isKnownSocialNetwork(link.network)) {
    return resolveSocialIconUrl(link.network, iconVariant);
  }

  return '';
}

export function normalizeSocialBlockLinks(
  links: SocialBlockLink[],
): SocialBlockLink[] {
  return links.map(link => ({
    ...link,
    url: link.url ?? '',
    icon: link.icon ?? null,
  }));
}

export function resolveSocialLinkAlt(link: SocialBlockLink): string {
  const trimmed = link.alt?.trim();
  if (trimmed && trimmed.length > 0) {
    return trimmed;
  }
  if (isKnownSocialNetwork(link.network)) {
    return SOCIAL_LINK_DEFAULTS[link.network].alt;
  }
  return CUSTOM_SOCIAL_LINK_DEFAULTS.alt;
}

export type { SocialPresetNetwork } from './social-link-defaults';
export {
  CUSTOM_SOCIAL_LINK_DEFAULTS,
  MIN_SOCIAL_BLOCK_LINKS,
  SOCIAL_PRESET_NETWORKS,
  SOCIAL_LINK_DEFAULTS,
} from './social-link-defaults';